import React, { useCallback, useEffect, useState } from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { Upload, RotateCcw, Check, Ban, FileCheck, Clock, Send, ShieldAlert, LogOut, X } from "lucide-react";

import Skeleton from "@/components/shared/Skeleton";
import { formatDate } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

import { ComplianceService } from "@/services/complianceService";
import useComplianceLiveUpdates from "../../hooks/useComplianceLiveUpdates";

/**
 * Full audit history for one compliance candidate (Phase 3).
 *
 * Replaces the per-document history drawer. A document-scoped trail could not
 * show candidate-level events (being sent to compliance), and it broke apart
 * when a document was replaced — the earlier upload's history lived on a
 * superseded row the drawer never looked at. This reads one endpoint that
 * already merges both, oldest-first.
 *
 * Everything rendered here comes from the server: it does NOT derive events from
 * current state the way the old drawer had to before the endpoint existed. If a
 * row is absent, it genuinely was not audited.
 */

// Presentation per action. Keys are the backend's past-tense vocabulary — the
// API translates internal names (UPLOAD_DOCUMENT -> UPLOADED) so this map never
// has to know about storage naming.
const ACTION_META = {
  SENT_TO_COMPLIANCE: { label: "Sent to Compliance", icon: Send, color: "#0ea5e9" },
  UPLOADED: { label: "Document Uploaded", icon: Upload, color: "#3b82f6" },
  REPLACED: { label: "Document Replaced", icon: Upload, color: "#6366f1" },
  REJECTED: { label: "Document Rejected", icon: Ban, color: "#ef4444" },
  // Phase 7C. Deliberately distinct from REJECTED: this is the candidate as a
  // whole failing compliance, and a timeline that labelled both "Rejected"
  // would read as though one more document had been turned down.
  CANDIDATE_REJECTED: {
    label: "Candidate Rejected",
    icon: ShieldAlert,
    color: "#dc2626",
  },
  // Phase C. A release ends the run and judges nobody, so it is neither red nor
  // green: a timeline that coloured it like CANDIDATE_REJECTED would accuse
  // someone who was simply moved to another job.
  CANDIDATE_RELEASED: {
    label: "Compliance run released",
    icon: LogOut,
    color: "#64748b",
  },
  RE_REQUESTED: { label: "Re-requested", icon: RotateCcw, color: "#f59e0b" },
  APPROVED: { label: "Approved", icon: Check, color: "#22c55e" },
  FIELDS_CONFIRMED: { label: "Fields Confirmed", icon: FileCheck, color: "#a855f7" },
};

// Unknown actions still render, labelled with whatever the server sent, so a
// newly-added action never shows as a blank row.
const metaFor = (action) => ACTION_META[action] ?? { label: action ?? "Event", icon: Clock, color: "#6b7280" };

// The actor chip is the point of the log: a candidate acting through their
// portal link must be visibly distinct from a staff member, because those carry
// very different weight when someone audits a decision.
//
// Only the three non-staff actor kinds are special-cased. Staff roles are
// tenant-defined rows ("Admin", "Recruiter", "Hiring Manager", …), so the server
// sends the user's real role name and it is rendered verbatim — never collapsed
// into a generic "Officer", which told the reader nothing about who acted.
const ROLE_CHIP = {
  candidate: { label: "Candidate", className: "bg-tone-blue-soft text-tone-blue border-tone-blue/30" },
  superadmin: {
    label: "Platform Admin",
    className: "bg-tone-purple-soft text-tone-purple border-tone-purple/30",
  },
  system: {
    label: "System",
    className: "bg-background-tertiary text-text-secondary border-border-primary",
  },
};

const roleChip = (role) =>
  ROLE_CHIP[role] ?? {
    // A tenant role name, shown as authored. Underscores are tolerated in
    // case a role was named that way; blank only if the user has no role
    // assigned yet (role_id is nullable until an admin assigns one).
    label: (role ?? "").replace(/_/g, " ") || "Staff",
    className: "bg-background-form/60 text-text-primary/70 border-border-primary/60",
  };

const CandidateAuditLogModal = ({ open, onClose, complianceCandidateId, candidateName }) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);

  // `silent` skips the skeleton: a live refresh should not blank a timeline the
  // user is reading. Only the initial open shows loading state.
  const load = useCallback(
    (silent = false) => {
      if (!complianceCandidateId) return undefined;

      let active = true;
      if (!silent) setLoading(true);
      setError(false);

      ComplianceService.getCandidateAuditLog(complianceCandidateId)
        .then((res) => {
          if (!active) return;
          setEvents(res?.history ?? []);
          setTruncated(Boolean(res?.truncated));
        })
        .catch(() => {
          if (!active) return;
          // Distinguish "failed to load" from "nothing recorded" —
          // showing an empty timeline on a network error would assert
          // that nothing ever happened to this candidate. On a silent
          // refresh, keep what is already on screen.
          if (!silent) {
            setEvents([]);
            setError(true);
          }
        })
        .finally(() => {
          if (active && !silent) setLoading(false);
        });

      return () => {
        active = false;
      };
    },
    [complianceCandidateId],
  );

  useEffect(() => {
    if (!open) return undefined;
    return load();
  }, [open, load]);

  // Keep an OPEN log current: an approval or re-upload by a colleague appends a
  // row the reader should see without closing and reopening. Only while open —
  // a closed modal refetching would be pure waste, and the `enabled` flag also
  // stops a background timer from firing into an unmounted tree.
  //
  // No dedicated WS event is needed: every action that writes an audit row
  // already emits one of the compliance events, so the existing feed is
  // exactly the right trigger. The dedupe key must be dropped first or the 2s
  // promise cache replays the pre-event response.
  useComplianceLiveUpdates({
    enabled: Boolean(open && complianceCandidateId),
    keys: [`getCandidateAuditLog_${complianceCandidateId}`],
    filter: (envelope) => {
      // Config events (checklist/settings) write no candidate audit row.
      const resource = envelope?.resource;
      if (resource !== "compliance_candidate" && resource !== "compliance_document") {
        return false;
      }
      // A document event carries its owning candidate in `parent`; a
      // candidate event is keyed on that candidate directly.
      const own = envelope?.parent?.compliance_candidate ?? envelope?.id;
      return own === complianceCandidateId;
    },
    onRefresh: () => load(true),
  });

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose?.()}>
      <DialogContent className="w-[560px] md:w-full max-w-[620px] flex flex-col p-0 max-h-[85vh]">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Audit History</DialogTitle>
          <DialogDescription className="text-xs text-text-primary/70 mt-0.5 truncate">
            {candidateName ? `Every recorded action for ${candidateName}` : "Every recorded action for this candidate"}
          </DialogDescription>
          <DialogClose className="cursor-pointer absolute right-5 top-4 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="px-5 py-4 overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex flex-col gap-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-xs text-text-primary/55 py-8 text-center">Could not load the audit history. Please try again.</p>
          ) : events.length === 0 ? (
            <p className="text-xs text-text-primary/55 py-8 text-center">No actions recorded for this candidate yet.</p>
          ) : (
            <>
              {truncated && (
                <p className="mb-3 rounded-lg border border-border-primary bg-background-form/40 px-3 py-2 text-[11px] text-text-primary/60">
                  Showing the most recent events. This candidate has a longer history than can be displayed at once.
                </p>
              )}

              <div className="flex flex-col">
                {events.map((event, i) => {
                  const meta = metaFor(event.action);
                  const Icon = meta.icon;
                  const chip = roleChip(event.actor?.role);
                  const last = i === events.length - 1;

                  return (
                    <div key={event.id ?? i} className="flex gap-3">
                      {/* Rail: dot + connector to the next event */}
                      <div className="flex flex-col items-center">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border"
                          style={{
                            background: `${meta.color}1a`,
                            borderColor: `${meta.color}33`,
                          }}
                        >
                          <Icon size={13} style={{ color: meta.color }} />
                        </div>
                        {!last && <div className="w-px flex-1 min-h-[18px] bg-border-primary my-1" />}
                      </div>

                      <div className={cn("min-w-0 flex-1", !last && "pb-4")}>
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-xs font-medium text-text-primary">{meta.label}</p>
                          {event.timestamp && <span className="text-[10px] text-text-secondary shrink-0 whitespace-nowrap">{formatDate(event.timestamp)}</span>}
                        </div>

                        {/* Which document — the log spans all of them, so
                                                    without this the timeline is ambiguous. */}
                        {event.documentName && <p className="text-[10.5px] text-text-primary/70 mt-0.5 truncate">{event.documentName}</p>}

                        {/* Which job (Phase G). A candidate on two jobs
                            produced two identical "Sent to compliance" rows,
                            and the reader could not tell which application
                            either was about. Only run-level events carry it,
                            so a document event is unchanged. */}
                        {event.owningJobTitle && <p className="text-[10.5px] text-text-primary/70 mt-0.5 truncate">for {event.owningJobTitle}</p>}

                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-[10.5px] text-text-secondary">by {event.actor?.name || "Unknown"}</span>
                          <span
                            className={cn(
                              // No `capitalize`: role names are
                              // tenant-authored, so they render
                              // exactly as the tenant wrote them.
                              "px-1.5 py-0.5 rounded-md border text-[9.5px] font-semibold",
                              chip.className,
                            )}
                          >
                            {chip.label}
                          </span>
                        </div>

                        {/* The "why": rejection reason / re-request text. */}
                        {event.note && (
                          <p className="mt-1 rounded-lg border border-border-primary bg-background-form/30 px-2.5 py-1.5 text-[11px] text-text-primary/80 whitespace-pre-wrap break-words">
                            {event.note}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateAuditLogModal;
